import React, { ReactElement, ReactNode } from 'react';
import { ExternalIdDTO, WorkDTO } from '../../../../api/core/models';
import { Actions, MultiInputControls } from 'pages/components/form/MultiInput/MultiInputControls';
import { getDefaultExternalId } from '../../../../Settings/emptyModels';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Panel } from '../layout/Panel';
import { SelectItem } from 'primereact/selectitem';
import { EnrichedValues } from '../../../../util/types';

interface ExternalIdsProps {
  title: string;
  label: string;
  extIds: ExternalIdDTO[];
  onChange: (subject: ExternalIdDTO[]) => void;
  options: SelectItem[];
  validation: ValidationResult;
  checkPermission: CheckPermissionFunction;
  autoFocus: boolean;
  enrichedValues?: EnrichedValues;
}

export function ExternalIds(props: ExternalIdsProps): ReactNode {
  return (
    <Panel title={props.title} toggleable={true}>
      <MultiInputControls<ExternalIdDTO>
        canEdit={props.checkPermission().canWrite}
        lines={props.extIds || []}
        onChange={props.onChange}
        addNew={(rows: ExternalIdDTO[]): number => rows.push(getDefaultExternalId())}
        renderLines={(
          externalIdDTO: ExternalIdDTO,
          onLineChange: (newValues: ExternalIdDTO) => void,
          actions: Actions,
          idx: number
        ): ReactElement => {
          const isNew = (
              props.enrichedValues?.externalIds as ExternalIdDTO[] | undefined
            )?.some(
            (enrichedExternalId: ExternalIdDTO): boolean =>
              enrichedExternalId.externalId === externalIdDTO.externalId &&
              enrichedExternalId.type === externalIdDTO.type
          ) ?? false;
          return (
            <InputGroup
              delete={{
                action: actions.delete,
                disabled: !props.checkPermission().canWrite,
              }}
            >
              <LabelledDropdown
                id={`externalId-${idx}-type`}
                autoFocus={props.autoFocus}
                value={externalIdDTO.type}
                className={'w-full'}
                wrapperClassName={'flex-1 min-w-0'}
                permissions={props.checkPermission('type')}
                validation={props.validation?.children[idx]?.fields?.type}
                onChange={(type: any): void => onLineChange({ ...externalIdDTO, type })}
                options={props.options}
                label={`${props.label}.attr.type`}
                isNew={isNew}
              />
              <LabelledInputText
                id={`externalId-${idx}-id`}
                className={'w-full'}
                wrapperClassName={'flex-1 min-w-0'}
                value={externalIdDTO.externalId}
                permissions={props.checkPermission('external_id')}
                validation={props.validation?.children[idx]?.fields?.external_id}
                disabled={!externalIdDTO.type}
                onChange={(externalId: string): void => {
                  switch(externalIdDTO.type) {
                    case 'isbn': return onLineChange({ ...externalIdDTO, externalId: externalId?.trim().toUpperCase().replace(/[^0-9X-]/gi, '') || null });
                    default: return onLineChange({ ...externalIdDTO, externalId });
                  }
                }}
                label={`${props.label}.attr.id`}
                isNew={isNew}
              />
            </InputGroup>
          )
        }}
      />
    </Panel>
  );
}
